import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Check, AlertCircle, Calendar, CheckSquare, MessageSquare, Trash2, Sparkles, FileText, Target, AlertTriangle, Zap, Settings, CreditCard, ChevronDown, ChevronUp, MapPin, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { api, API_BASE } from '../../utils/api';

const NotificationCenter = ({ user, triggerClassName }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [briefingModalOpen, setBriefingModalOpen] = useState(false);
  const [briefingData, setBriefingData] = useState(null);
  const [generatingBriefing, setGeneratingBriefing] = useState(false);
  const [expandedDays, setExpandedDays] = useState(new Set(['Today']));

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'account_health':
        return <AlertCircle className="h-4 w-4 text-amber-500" />;
      case 'renewal_reminder':
        return <Calendar className="h-4 w-4 text-blue-500" />;
      case 'task_alert':
        return <CheckSquare className="h-4 w-4 text-purple-500" />;
      case 'communication_alert':
        return <MessageSquare className="h-4 w-4 text-green-500" />;
      case 'daily_briefing':
        return <Sparkles className="h-4 w-4 text-purple-500" />;
      case 'system_alert':
        return <Settings className="h-4 w-4 text-muted-foreground" />;
      case 'workflow_alert':
        return <Zap className="h-4 w-4 text-yellow-500" />;
      case 'campaign_alert':
        return <FileText className="h-4 w-4 text-indigo-500" />;
      case 'subscription_alert':
        return <CreditCard className="h-4 w-4 text-green-600" />;
      default:
        return <Bell className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 border-red-200 dark:border-red-700';
      case 'high':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400 border-amber-200 dark:border-amber-700';
      case 'medium':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400 border-amber-200 dark:border-amber-700';
      default:
        return 'bg-muted text-foreground border-gray-200 dark:border-gray-600';
    }
  };

  /** Group notifications by day label (Today, Yesterday, or date). */
  const groupByDay = (items) => {
    const groups = {};
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    for (const item of items) {
      const d = new Date(item.created_at);
      const ds = d.toDateString();
      const label = ds === today ? 'Today' : ds === yesterday ? 'Yesterday'
        : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      if (!groups[label]) groups[label] = [];
      groups[label].push(item);
    }
    return groups;
  };

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = async (notificationIds) => {
    try {
      const response = await api.post('/notifications/mark-read', { notification_ids: notificationIds });
      // Update local state
      setNotifications(prev => prev.map(n => 
        notificationIds.includes(n.id) ? { ...n, is_read: true } : n
      ));
      // Use server-provided unread count for accuracy
      if (response.data.unread_count !== undefined) {
        setUnreadCount(response.data.unread_count);
      }
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const response = await api.delete(`/notifications/${notificationId}`);
      // Update local state - remove the deleted notification
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      // Use server-provided unread count for accuracy
      if (response.data.unread_count !== undefined) {
        setUnreadCount(response.data.unread_count);
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.is_read) {
      markAsRead([notification.id]);
    }

    // Navigate to action URL if provided (restrict to same-origin paths)
    if (notification.action_url) {
      let url = notification.action_url;
      // Ensure /app prefix for app routes (handles legacy notifications)
      if (url.startsWith('/') && !url.startsWith('/app/') && !url.startsWith('//')) {
        url = '/app' + url;
      }
      if (url.startsWith('/') && !url.startsWith('//')) {
        navigate(url);
      } else {
        console.warn('Blocked navigation to external URL:', url);
      }
    }

    setIsOpen(false);
  };

  const markAllAsRead = async () => {
    const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
    if (unreadIds.length > 0) {
      await markAsRead(unreadIds);
    }
  };

  const dismissAllNotifications = async () => {
    try {
      await api.delete('/notifications');
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error dismissing all notifications:', error);
    }
  };

  const generateDailyBriefing = async () => {
    setGeneratingBriefing(true);
    try {
      const response = await api.post('/notifications/daily-briefing');
      if (response.data.success && response.data.briefing) {
        setBriefingData(response.data.briefing.metadata?.full_briefing || response.data.briefing);
        setBriefingModalOpen(true);
        // Refresh notifications to show the new briefing
        fetchNotifications();
      }
    } catch (error) {
      console.error('Error generating daily briefing:', error);
    } finally {
      setGeneratingBriefing(false);
    }
  };

  const openBriefingModal = (notification) => {
    if (notification.metadata?.full_briefing) {
      setBriefingData(notification.metadata.full_briefing);
      setBriefingModalOpen(true);
      // Mark as read
      if (!notification.is_read) {
        markAsRead([notification.id]);
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // SSE real-time connection with polling fallback
  const sseRef = useRef(null);
  const sseFailCountRef = useRef(0);
  const fallbackIntervalRef = useRef(null);
  const isOpenRef = useRef(isOpen);
  const debounceTimerRef = useRef(null);

  // Keep isOpenRef in sync so polling callback sees latest value
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Debounced fetch: collapses rapid SSE events into a single API call
  const debouncedFetch = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      fetchNotifications();
      debounceTimerRef.current = null;
    }, 500);
  }, [fetchNotifications]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const connectSSE = async () => {
      // Close existing connection if any
      if (sseRef.current) {
        sseRef.current.close();
      }

      // Exchange JWT for a short-lived SSE nonce
      let sseToken;
      try {
        const res = await api.post('/notifications/stream-token');
        sseToken = res.data.token;
      } catch (err) {
        console.error('Failed to obtain SSE token:', err);
        sseFailCountRef.current += 1;
        if (sseFailCountRef.current < 3) {
          const delay = Math.min(1000 * Math.pow(2, sseFailCountRef.current), 30000);
          setTimeout(connectSSE, delay);
        } else {
          startFallbackPolling();
        }
        return;
      }

      const sseUrl = `${API_BASE}/notifications/stream?token=${encodeURIComponent(sseToken)}`;
      const eventSource = new EventSource(sseUrl);
      sseRef.current = eventSource;

      eventSource.addEventListener('connected', () => {
        console.debug('[SSE] Connected to notification stream');
        sseFailCountRef.current = 0;
        // SSE connected — disable polling fallback
        if (fallbackIntervalRef.current) {
          clearInterval(fallbackIntervalRef.current);
          fallbackIntervalRef.current = null;
        }
        // Immediately fetch current notifications so badge is accurate
        fetchNotifications();
      });

      eventSource.addEventListener('notification', (event) => {
        console.debug('[SSE] New notification event:', event.data);
        // A new notification was created — debounced refresh
        debouncedFetch();
        // Broadcast to other hooks (e.g. usePendingActions) so they can refresh without polling
        window.dispatchEvent(new CustomEvent('sse:notification', { detail: event.data }));
      });

      eventSource.onerror = (err) => {
        console.warn('[SSE] Connection error, closing. ReadyState:', eventSource.readyState);
        eventSource.close();
        sseRef.current = null;
        sseFailCountRef.current += 1;

        if (sseFailCountRef.current >= 3) {
          startFallbackPolling();
        } else {
          // Retry SSE with exponential backoff
          const delay = Math.min(1000 * Math.pow(2, sseFailCountRef.current), 30000);
          setTimeout(connectSSE, delay);
        }
      };
    };

    const startFallbackPolling = () => {
      if (!fallbackIntervalRef.current) {
        fallbackIntervalRef.current = setInterval(() => {
          if (!isOpenRef.current) {
            fetchNotifications();
          }
        }, 30000);
      }
    };

    connectSSE();

    return () => {
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
      if (fallbackIntervalRef.current) {
        clearInterval(fallbackIntervalRef.current);
        fallbackIntervalRef.current = null;
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [fetchNotifications, debouncedFetch]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={`relative ${triggerClassName || 'text-foreground hover:text-foreground dark:hover:text-white'}`}
        aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs">
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </Button>

      {/* Notification Drawer */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
          <SheetHeader className="px-5 pr-12 py-4 border-b border-border flex-shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-base font-semibold text-foreground">Notifications</SheetTitle>
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={generateDailyBriefing}
                  disabled={generatingBriefing}
                  className="text-xs text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300"
                  aria-label="Generate AI daily briefing"
                  aria-busy={generatingBriefing}
                >
                  <Sparkles className={`h-3 w-3 mr-1 ${generatingBriefing ? 'animate-spin' : ''}`} />
                  {generatingBriefing ? 'Generating...' : 'AI Briefing'}
                </Button>
                {notifications.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={dismissAllNotifications}
                    title="Dismiss all"
                    className="text-red-600 hover:text-red-700"
                    aria-label="Dismiss all notifications"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={markAllAsRead}
                    title="Mark all read"
                    className="text-blue-600 hover:text-blue-700"
                    aria-label="Mark all notifications as read"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <SheetDescription className="sr-only">Notification list grouped by date</SheetDescription>
          </SheetHeader>

          {/* Notifications List — grouped by date */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">
                <Bell className="h-6 w-6 mx-auto mb-2 animate-pulse" />
                <p className="text-sm">Loading...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Bell className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm">No notifications</p>
                <p className="text-xs mt-1">You're all caught up!</p>
              </div>
            ) : (
              <div className="py-2">
                {Object.entries(groupByDay(notifications)).map(([dayLabel, dayNotifications]) => {
                  const isDayExpanded = expandedDays.has(dayLabel);
                  const toggleDay = () => setExpandedDays(prev => {
                    const next = new Set(prev);
                    next.has(dayLabel) ? next.delete(dayLabel) : next.add(dayLabel);
                    return next;
                  });
                  const unreadInGroup = dayNotifications.filter(n => !n.is_read).length;

                  return (
                    <div key={dayLabel} className="mb-1">
                      {/* Day header */}
                      <button
                        onClick={toggleDay}
                        className="flex items-center gap-1.5 w-full px-5 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground hover:bg-muted/50 transition-colors"
                      >
                        {isDayExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3 -rotate-90" />}
                        <span>{dayLabel}</span>
                        <span className="text-muted-foreground/60">({dayNotifications.length})</span>
                        {unreadInGroup > 0 && (
                          <span className="ml-auto text-2xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full">
                            {unreadInGroup} new
                          </span>
                        )}
                      </button>

                      {/* Notifications for this day */}
                      {isDayExpanded && (
                        <div>
                          {dayNotifications.map((notification) => (
                            <div
                              key={notification.id}
                              onClick={() => notification.type === 'daily_briefing' ? openBriefingModal(notification) : handleNotificationClick(notification)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); notification.type === 'daily_briefing' ? openBriefingModal(notification) : handleNotificationClick(notification); } }}
                              role="button"
                              tabIndex={0}
                              aria-label={notification.title}
                              className={`px-5 py-3 hover:bg-muted/50 cursor-pointer transition-colors border-b border-border/50 ${
                                !notification.is_read ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''
                              } ${notification.type === 'daily_briefing' ? 'bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-l-3 border-l-blue-500' : ''}`}
                            >
                              <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0 mt-0.5">
                                  {getNotificationIcon(notification.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <p className={`text-sm text-foreground truncate ${!notification.is_read ? 'font-semibold' : 'font-medium'}`}>
                                      {notification.title}
                                    </p>
                                    {!notification.is_read && (
                                      <div className="h-2 w-2 bg-blue-600 rounded-full ml-2 flex-shrink-0"></div>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                    {notification.message}
                                  </p>
                                  <div className="flex items-center space-x-2 mt-1.5">
                                    <Badge
                                      variant="outline"
                                      className={`text-2xs px-1.5 py-0 ${getPriorityColor(notification.priority)}`}
                                    >
                                      {notification.priority}
                                    </Badge>
                                    <span className="text-2xs text-muted-foreground">
                                      {new Date(notification.created_at).toLocaleTimeString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </span>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteNotification(notification.id);
                                  }}
                                  className="h-7 w-7 text-muted-foreground hover:text-muted-foreground flex-shrink-0"
                                  aria-label={`Delete notification: ${notification.title}`}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Daily Briefing Modal */}
      {briefingModalOpen && briefingData && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={() => setBriefingModalOpen(false)}
            aria-hidden="true"
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" role="dialog" aria-modal="true" aria-labelledby="briefing-modal-title">
              {/* Modal Header */}
              <div className="bg-brand-gradient p-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Sparkles className="h-8 w-8" />
                    <div>
                      <h2 className="text-xl font-bold" id="briefing-modal-title">Your Daily Briefing</h2>
                      <p className="text-blue-100 text-sm">{briefingData.greeting}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setBriefingModalOpen(false)}
                    className="text-white hover:bg-white/20"
                    aria-label="Close daily briefing"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Summary */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <p className="text-foreground">{briefingData.summary}</p>
                </div>

                {/* Priority Actions */}
                {briefingData.priority_actions && briefingData.priority_actions.length > 0 && (
                  <div>
                    <h3 className="flex items-center space-x-2 text-lg font-semibold text-foreground mb-3">
                      <Target className="h-5 w-5 text-blue-600" />
                      <span>Priority Actions</span>
                    </h3>
                    <div className="space-y-3">
                      {briefingData.priority_actions.map((action, idx) => (
                        <div key={idx} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 shadow-sm">
                          <div className="flex items-start space-x-3">
                            <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {action.priority || idx + 1}
                            </span>
                            <div className="flex-1">
                              <p className="font-medium text-foreground">{action.action}</p>
                              {action.account && (
                                <p className="text-sm text-blue-600 mt-1 inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{action.account}</p>
                              )}
                              <p className="text-sm text-muted-foreground mt-1">{action.reason}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Risk Alerts */}
                {briefingData.risk_alerts && briefingData.risk_alerts.length > 0 && (
                  <div>
                    <h3 className="flex items-center space-x-2 text-lg font-semibold text-foreground mb-3">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      <span>Risk Alerts</span>
                    </h3>
                    <div className="space-y-3">
                      {briefingData.risk_alerts.map((alert, idx) => (
                        <div key={idx} className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                          <p className="font-medium text-amber-800 dark:text-amber-400">{alert.account}</p>
                          <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">{alert.issue}</p>
                          <p className="text-sm text-muted-foreground mt-2 italic inline-flex items-center gap-1"><Lightbulb className="h-3.5 w-3.5" />{alert.recommendation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Wins */}
                {briefingData.quick_wins && briefingData.quick_wins.length > 0 && (
                  <div>
                    <h3 className="flex items-center space-x-2 text-lg font-semibold text-foreground mb-3">
                      <Zap className="h-5 w-5 text-amber-500" />
                      <span>Quick Wins</span>
                    </h3>
                    <ul className="space-y-2">
                      {briefingData.quick_wins.map((win, idx) => (
                        <li key={idx} className="flex items-center space-x-2 text-foreground">
                          <Check className="h-4 w-4 text-green-500" />
                          <span>{win}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Closing Note */}
                {briefingData.closing_note && (
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-lg p-4 text-center">
                    <p className="text-blue-700 dark:text-blue-300 italic">{briefingData.closing_note}</p>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="border-t dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-700">
                <Button
                  onClick={() => setBriefingModalOpen(false)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Got it, let's go!
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationCenter;
